import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Download, X, Share, Plus } from "lucide-react";
import { useInstall } from "@/context/InstallContext";

const DISMISS_KEY = "pt-install-dismissed";

export const InstallBanner = () => {
  const { canInstall, promptInstall, isIOS, installed } = useInstall();
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    setDismissed(!!localStorage.getItem(DISMISS_KEY));
  }, []);

  const showIOS = isIOS && !canInstall;
  const visible = !installed && !dismissed && (canInstall || showIOS);

  const dismiss = () => {
    setDismissed(true);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    await promptInstall();
    dismiss();
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          data-testid="install-banner"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-4 left-4 right-4 z-[60] md:left-auto md:right-6 md:max-w-md"
        >
          <div className="relative bg-black/90 backdrop-blur-xl border border-gold/40 shadow-2xl p-4 pr-10">
            <button
              data-testid="install-dismiss"
              onClick={dismiss}
              aria-label="Dismiss"
              className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
            >
              <X size={18} />
            </button>

            <div className="flex items-start gap-3">
              <span className="w-11 h-11 shrink-0 bg-gold text-black flex items-center justify-center">
                <Download size={20} />
              </span>
              <div className="flex-1">
                <p className="font-display text-lg text-white leading-tight">Add Perfect Touch to your phone</p>
                {showIOS ? (
                  <p className="mt-1.5 text-sm text-zinc-400 leading-relaxed">
                    Tap the <Share size={13} className="inline -mt-0.5 text-gold" /> Share button, then
                    <span className="text-white"> "Add to Home Screen" </span>
                    <Plus size={13} className="inline -mt-0.5 text-gold" /> for one-tap booking.
                  </p>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-zinc-400">Install our app for fast quotes and one-tap booking — no app store needed.</p>
                    <button
                      data-testid="install-accept"
                      onClick={install}
                      className="mt-3 bg-gold text-black text-sm font-semibold px-5 py-2.5 hover:bg-[#c5a059] transition-colors duration-300 flex items-center gap-2"
                    >
                      <Download size={16} /> Install App
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
